import useTranslation from "@babackapp/translations/use-translation";
import { type ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import { useMemo } from "react";
import { SizingRecommendationBadge } from "./sizing-recommendation-badge";

const THUMBNAIL_SIZE = 56;

function getThumbnailUrl(url: string): string {
  if (!url.includes("cdn.shopify.com")) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("width", String(THUMBNAIL_SIZE));
    return parsed.toString();
  } catch {
    return url;
  }
}

type SizingDiagnosticRow = {
  productId: string;
  productTitle: string;
  imageUrl: string;
  recommendation: "runs_large" | "runs_small" | null;
  ratio: number | null;
  sampleSize: number;
  sizeTooLargeCount: number;
  sizeTooSmallCount: number;
};

export function useSizingTableColumns(): ColumnDef<SizingDiagnosticRow>[] {
  const t = useTranslation();

  return useMemo(
    () => [
      {
        accessorKey: "productTitle",
        header: t("sizing.table.product"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.imageUrl && (
              <img
                src={getThumbnailUrl(row.original.imageUrl)}
                alt={row.original.productTitle}
                width={28}
                height={28}
                loading="lazy"
                decoding="async"
                className="h-7 w-7 rounded object-cover"
              />
            )}
            <span className="font-medium truncate max-w-[220px]">
              {row.original.productTitle}
            </span>
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "recommendation",
        header: t("sizing.table.recommendation"),
        cell: ({ row }) => (
          <SizingRecommendationBadge
            recommendation={row.original.recommendation}
          />
        ),
        enableSorting: false,
      },
      {
        id: "trend",
        accessorFn: (row) =>
          row.sampleSize > 0
            ? Math.max(row.sizeTooLargeCount, row.sizeTooSmallCount) /
              row.sampleSize
            : null,
        header: ({ column }) => (
          <button
            type="button"
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("sizing.table.trend")}
            <ArrowUpDown className="h-3.5 w-3.5" />
          </button>
        ),
        cell: ({ row }) => {
          const { sampleSize, sizeTooLargeCount, sizeTooSmallCount } =
            row.original;
          if (sampleSize === 0) return "—";
          const smallPct = (sizeTooSmallCount / sampleSize) * 100;
          const largePct = 100 - smallPct;
          return (
            <div className="w-[180px]">
              <div className="flex h-2 overflow-hidden rounded bg-muted">
                <div className="bg-primary" style={{ width: `${smallPct}%` }} />
                <div className="bg-warning" style={{ width: `${largePct}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-xs text-muted-foreground tabular-nums">
                <span>
                  {t("sizing.table.trendSmall")} {smallPct.toFixed(0)}%
                </span>
                <span>
                  {largePct.toFixed(0)}% {t("sizing.table.trendLarge")}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "sampleSize",
        header: ({ column }) => (
          <button
            type="button"
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("sizing.table.sampleSize")}
            <ArrowUpDown className="h-3.5 w-3.5" />
          </button>
        ),
        cell: ({ row }) => row.original.sampleSize,
      },
    ],
    [t],
  );
}
